import {Dispatch, FC, ReactNode, SetStateAction, useEffect, useState} from 'react'
import {numberWithCommas} from '../../../../../../_metronic/helpers'
import {CustomInputs} from '../../../../../../_metronic/helpers/components/CustomInputs'

type Props = {
  disabled?: boolean
  readOnly?: boolean
  title?: string
  showQuantityDecimalPlaces?: boolean
  showQuantityStep?: boolean
  quantityDecimalPlaces: string | number
  setQuantityDecimalPlaces?: Dispatch<SetStateAction<string>>
  quantityStep: string | number
  setQuantityStep?: Dispatch<SetStateAction<string>>
  minQuantity: string | number
  setMinQuantity?: Dispatch<SetStateAction<string>>
  maxQuantity: string | number
  setMaxQuantity?: Dispatch<SetStateAction<string>>
  errors?: {
    quantityDecimalPlaces?: ReactNode
    quantityStep?: ReactNode
    minQuantity?: ReactNode
    maxQuantity?: ReactNode
  }
}

const getMinimumQuantityStepText = (decimalPlaces: number) => {
  if (!Number.isInteger(decimalPlaces) || decimalPlaces < 0 || decimalPlaces > 8) {
    return '0.00000001'
  }

  if (decimalPlaces === 0) {
    return '1'
  }

  return `0.${'0'.repeat(decimalPlaces - 1)}1`
}

const toPlainDecimalText = (value: string | number | undefined | null, emptyLabel = '') => {
  if (value === undefined || value === null || value === '') {
    return emptyLabel
  }

  const text = value.toString()
  if (!/e/i.test(text)) {
    return text
  }

  const numberValue = Number(value)
  if (!Number.isFinite(numberValue)) {
    return text
  }

  return numberValue.toFixed(8).replace(/\.?0+$/, '') || '0'
}

const displayValue = (value: string | number | undefined | null, emptyLabel = '-') => {
  const text = toPlainDecimalText(value, emptyLabel)
  return text === emptyLabel ? emptyLabel : numberWithCommas(text)
}

const AutomaticInvoiceAdvancedSettings: FC<Props> = ({
  disabled = false,
  readOnly = false,
  title = 'تنظیمات پیشرفته',
  showQuantityDecimalPlaces = true,
  showQuantityStep = true,
  quantityDecimalPlaces,
  setQuantityDecimalPlaces,
  quantityStep,
  setQuantityStep,
  minQuantity,
  setMinQuantity,
  maxQuantity,
  setMaxQuantity,
  errors,
}) => {
  const [open, setOpen] = useState(false)
  const visibleFieldCount = [showQuantityDecimalPlaces, showQuantityStep, true, true].filter(Boolean)
    .length
  const fieldClassName = visibleFieldCount <= 2 ? 'col-lg-6 col-md-6' : 'col-lg-3 col-md-6'
  const quantityDecimalPlacesValue = Number(quantityDecimalPlaces)
  const minimumQuantityStepText = getMinimumQuantityStepText(quantityDecimalPlacesValue)

  useEffect(() => {
    if (
      readOnly ||
      !showQuantityDecimalPlaces ||
      !showQuantityStep ||
      !setQuantityStep ||
      !Number.isInteger(quantityDecimalPlacesValue) ||
      quantityDecimalPlacesValue < 0 ||
      quantityDecimalPlacesValue > 8
    ) {
      return
    }

    if (toPlainDecimalText(quantityStep) !== minimumQuantityStepText) {
      setQuantityStep(minimumQuantityStepText)
    }
  }, [
    minimumQuantityStepText,
    quantityDecimalPlacesValue,
    quantityStep,
    readOnly,
    setQuantityStep,
    showQuantityDecimalPlaces,
    showQuantityStep,
  ])

  return (
    <div className='rounded border border-dashed border-gray-300 bg-white px-4 py-3'>
      <button
        type='button'
        className='btn btn-sm btn-light w-100 d-flex align-items-center justify-content-between'
        onClick={() => setOpen((value) => !value)}
      >
        <span className='d-flex align-items-center gap-2 fw-bold text-gray-800'>
          <i className='las la-sliders-h fs-2 text-primary' />
          {title}
        </span>
        <i className={`las ${open ? 'la-angle-up' : 'la-angle-down'} fs-3 text-gray-600`} />
      </button>

      {open && (
        <div className='pt-4'>
          {readOnly ? (
            <div className='d-flex flex-wrap gap-2'>
              {showQuantityDecimalPlaces && (
                <span className='badge badge-light-info'>
                  اعشار مجاز مقدار: {displayValue(quantityDecimalPlaces)}
                </span>
              )}
              {showQuantityStep && (
                <span className='badge badge-light-primary'>
                  گام تغییر مقدار: {displayValue(quantityStep)}
                </span>
              )}
              <span className='badge badge-light-success'>
                حداقل مقدار: {displayValue(minQuantity)}
              </span>
              <span className='badge badge-light-warning'>
                حداکثر مقدار: {displayValue(maxQuantity, 'بدون سقف')}
              </span>
            </div>
          ) : (
            <div className='row g-4'>
              {showQuantityDecimalPlaces && (
                <div className={fieldClassName}>
                  <CustomInputs
                    type='number'
                    numberAsText
                    label='اعشار مجاز مقدار'
                    isRequired
                    isDisabled={disabled}
                    stateValue={{
                      value: quantityDecimalPlaces,
                      setValue: setQuantityDecimalPlaces || (() => {}),
                    }}
                    hint={<span className='fs-7 text-gray-600'>حداکثر ۸ رقم اعشار</span>}
                    error={errors?.quantityDecimalPlaces}
                  />
                </div>
              )}
              {showQuantityStep && (
                <div className={fieldClassName}>
                  <CustomInputs
                    type='number'
                    numberAsText
                    label='گام تغییر مقدار'
                    isRequired
                    isDisabled={disabled || showQuantityDecimalPlaces}
                    stateValue={{
                      value: toPlainDecimalText(quantityStep),
                      setValue: setQuantityStep || (() => {}),
                    }}
                    hint={
                      <span className='fs-7 text-gray-600'>
                        بر اساس تعداد اعشار، همیشه روی حداقل مجاز تنظیم می‌شود:{' '}
                        {minimumQuantityStepText}
                      </span>
                    }
                    error={errors?.quantityStep}
                  />
                </div>
              )}
              <div className={fieldClassName}>
                <CustomInputs
                  type='number'
                  numberAsText
                  label='حداقل مقدار قابل صدور'
                  isDisabled={disabled}
                  stateValue={{
                    value: minQuantity,
                    setValue: setMinQuantity || (() => {}),
                  }}
                  error={errors?.minQuantity}
                />
              </div>
              <div className={fieldClassName}>
                <CustomInputs
                  type='number'
                  numberAsText
                  label='حداکثر مقدار قابل صدور'
                  isDisabled={disabled}
                  stateValue={{
                    value: maxQuantity,
                    setValue: setMaxQuantity || (() => {}),
                  }}
                  hint={<span className='fs-7 text-gray-600'>خالی یا ۰ یعنی بدون سقف</span>}
                  error={errors?.maxQuantity}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export {AutomaticInvoiceAdvancedSettings, getMinimumQuantityStepText, toPlainDecimalText}
